// Fältvis sparning av konsultprofilen (profiles/{uid}).
//
// Sidan håller ett lokalt läge och en baslinje (det som senast sparats eller
// lästs). diffProfile räknar fram vad som ändrats, och buildUpdate gör om det
// till en uppdatering med fältsökvägar. Listor med rader (uppdrag m.fl.) har ett
// stabilt id per rad och skrivs i en transaktion mot dokumentets aktuella läge,
// så att andras samtidiga ändringar i andra rader eller fält blir kvar.

import { PROFILE_BLOCKS, EXPERIENCE_FIRST_ORDER } from '../config/profileBlocks';
import { same, diffRows, applyRows } from './radSync';

export { applyRows };

export const PERSONAL_KEYS = [
  'name', 'title', 'location', 'email', 'phone', 'photo', 'linkedin', 'shortName',
];
// Fritext på toppnivå
export const TEXT_FIELDS = ['ingress', 'project_scale', 'licenses'];
// Stränglistor – sparas med arrayUnion/arrayRemove
export const CHIP_FIELDS = [
  'competencies', 'standards', 'personal_profile', 'industries',
  'tools.expert', 'tools.good', 'tools.basic', 'visibleBlocks',
];
// Listor med rader – varje rad har ett stabilt id
export const LIST_FIELDS = [
  'assignments', 'employment', 'education', 'certifications',
  'languages', 'publications', 'references', 'board',
];


export function getPath(obj, path) {
  return path.split('.').reduce((o, k) => (o == null ? undefined : o[k]), obj);
}

export function newRowId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 10);
}

export const defaultVisibleBlocks = () =>
  PROFILE_BLOCKS.filter(b => b.defaultVisible).map(b => b.id);

export function emptyProfile() {
  return {
    personal: Object.fromEntries(PERSONAL_KEYS.map(k => [k, ''])),
    ingress: '',
    competencies: [],
    assignments: [],
    employment: [],
    education: [],
    certifications: [],
    tools: { expert: [], good: [], basic: [] },
    languages: [],
    standards: [],
    personal_profile: [],
    industries: [],
    project_scale: '',
    publications: [],
    references: [],
    licenses: '',
    board: [],
    profileType: 'experience',
    blockOrder: EXPERIENCE_FIRST_ORDER,
    visibleBlocks: defaultVisibleBlocks(),
  };
}

/** Plockar ut de fält sidan redigerar ur ett profildokument (eller en version). */
export function pickProfile(data) {
  const base = emptyProfile();
  const d = data || {};
  const out = { ...base };
  out.personal = { ...base.personal, ...(d.personal || {}) };
  TEXT_FIELDS.forEach(f => { if (d[f] !== undefined) out[f] = d[f]; });
  ['competencies', 'standards', 'personal_profile', 'industries'].forEach(f => {
    if (Array.isArray(d[f])) out[f] = d[f];
  });
  out.tools = { ...base.tools, ...(d.tools || {}) };
  LIST_FIELDS.forEach(f => { if (Array.isArray(d[f])) out[f] = d[f]; });
  // Uppdrag från före mjukvarufältet saknar software
  out.assignments = out.assignments.map(a => ({ software: [], ...a }));
  if (d.profileType) out.profileType = d.profileType;
  if (d.blockOrder) out.blockOrder = d.blockOrder;
  if (d.visibleBlocks) {
    out.visibleBlocks = [...new Set([...d.visibleBlocks, ...defaultVisibleBlocks()])];
  }
  return out;
}

export function missingRowIds(data) {
  return LIST_FIELDS.some(f => (data?.[f] || []).some(r => r && !r.id));
}

/** Ger rader utan id ett nytt id. Returnerar bara de listor som ändrats. */
export function rowIdPatch(data) {
  const patch = {};
  LIST_FIELDS.forEach(f => {
    const rows = data?.[f];
    if (Array.isArray(rows) && rows.some(r => r && !r.id)) {
      patch[f] = rows.map(r => (r && !r.id ? { ...r, id: newRowId() } : r));
    }
  });
  return patch;
}

export const withRowIds = data => ({ ...data, ...rowIdPatch(data) });

/** Rensa: tömmer profilen men behåller personuppgifterna och visningsvalen. */
export function clearedProfile(cur) {
  const base = emptyProfile();
  return {
    ...base,
    personal: { ...base.personal, ...(cur.personal || {}) },
    profileType: cur.profileType,
    blockOrder: cur.blockOrder,
    visibleBlocks: cur.visibleBlocks,
  };
}

/**
 * Vad som skiljer `after` från `before`.
 * keys: kvittensnycklar (vilka fält/rader som ska få bock).
 */
export function diffProfile(before, after) {
  const fields = {};
  const chips = {};
  const lists = {};
  const keys = new Set();

  new Set([...Object.keys(before.personal || {}), ...Object.keys(after.personal || {})])
    .forEach(k => {
      const a = before.personal?.[k] ?? '';
      const b = after.personal?.[k] ?? '';
      if (!same(a, b)) { fields['personal.' + k] = b; keys.add('personal.' + k); }
    });

  [...TEXT_FIELDS, 'profileType', 'blockOrder'].forEach(f => {
    if (!same(before[f] ?? '', after[f] ?? '')) {
      fields[f] = after[f] ?? '';
      keys.add(f === 'blockOrder' ? 'profileType' : f);
    }
  });

  CHIP_FIELDS.forEach(f => {
    const a = getPath(before, f) || [];
    const b = getPath(after, f) || [];
    const add = [...new Set(b.filter(x => !a.includes(x)))];
    const remove = [...new Set(a.filter(x => !b.includes(x)))];
    if (!add.length && !remove.length) return;
    chips[f] = { add, remove };
    if (f === 'visibleBlocks') [...add, ...remove].forEach(id => keys.add('visibleBlocks.' + id));
    else keys.add(f);
  });

  LIST_FIELDS.forEach(f => {
    // Uppdrag kvitteras på raden, övriga listor per fält
    const d = diffRows(f, before[f] || [], after[f] || [], { radKvittens: f === 'assignments' });
    if (d) { lists[f] = d.change; d.keys.forEach(k => keys.add(k)); }
  });

  const empty = !Object.keys(fields).length && !Object.keys(chips).length
    && !Object.keys(lists).length;
  return { fields, chips, lists, keys: [...keys], empty };
}

export function applyChips(remote, { add, remove }) {
  const kept = (remote || []).filter(x => !remove.includes(x));
  return [...kept, ...add.filter(x => !kept.includes(x))];
}

/** Listor, eller en stränglista som både får och tappar värden, kräver transaktion. */
export function needsTransaction(diff) {
  return Object.keys(diff.lists).length > 0
    || Object.values(diff.chips).some(c => c.add.length && c.remove.length);
}

/**
 * Uppdatering med fältsökvägar. `remote` är dokumentets aktuella läge
 * (i en transaktion) eller null när ingen transaktion behövs.
 */
export function buildUpdate(diff, remote, { arrayUnion, arrayRemove }) {
  const payload = { ...diff.fields };
  Object.entries(diff.chips).forEach(([f, c]) => {
    if (c.add.length && c.remove.length) payload[f] = applyChips(getPath(remote, f), c);
    else if (c.add.length) payload[f] = arrayUnion(...c.add);
    else payload[f] = arrayRemove(...c.remove);
  });
  Object.entries(diff.lists).forEach(([f, change]) => {
    payload[f] = applyRows(remote?.[f], change);
  });
  return payload;
}

// ── Varningar (stoppar inte sparning) ─────────────────────────────
export function fieldWarning(kind, value) {
  const v = (value || '').trim();
  if (!v) return null;
  if (kind === 'email') {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? null : 'Kontrollera e-postadressen';
  }
  if (kind === 'shortName') {
    return /^[A-ZÅÄÖ]{2,4}$/i.test(v) ? null : 'Förkortningen ska vara 2–4 bokstäver';
  }
  if (kind === 'linkedin') {
    return /linkedin\.com/i.test(v) ? null : 'Adressen ska gå till linkedin.com';
  }
  return null;
}
